/**
 * Thin React reader over the shared Energiatehokkuus building snapshot cache.
 * Prefer this from OptimizationBuildingSection; portfolio Energiatehokkuus stays separate.
 */
import { useEffect, useState } from "react";

import type { Building } from "@/lib/api/building";
import {
  getSharedEnergiatehokkuusBuildingSnapshotCache,
  type EnergiatehokkuusBuildingSnapshotBag,
} from "@/lib/energiatehokkuus-building-snapshot";

export type EnergiatehokkuusBuildingSnapshotHookState =
  | { status: "loading" }
  | { status: "ok"; bag: EnergiatehokkuusBuildingSnapshotBag };

function hookStateFromCache(
  buildingId: string,
): EnergiatehokkuusBuildingSnapshotHookState {
  const cached =
    getSharedEnergiatehokkuusBuildingSnapshotCache().getState(buildingId);
  if (cached.status === "ok") {
    return { status: "ok", bag: cached.bag };
  }
  return { status: "loading" };
}

export function useEnergiatehokkuusBuildingSnapshot(
  buildingId: string,
): EnergiatehokkuusBuildingSnapshotHookState {
  const [trackedBuildingId, setTrackedBuildingId] = useState(buildingId);
  const [state, setState] = useState<EnergiatehokkuusBuildingSnapshotHookState>(
    () => hookStateFromCache(buildingId),
  );

  if (trackedBuildingId !== buildingId) {
    setTrackedBuildingId(buildingId);
    setState(hookStateFromCache(buildingId));
  }

  useEffect(() => {
    const cache = getSharedEnergiatehokkuusBuildingSnapshotCache();
    const sync = () => setState(hookStateFromCache(buildingId));
    const unsubscribe = cache.subscribe(buildingId, sync);
    void cache.load(buildingId).then(sync);
    sync();
    return unsubscribe;
  }, [buildingId]);

  return state;
}

/** UI adapter: nested operational-loop reload for the selected equipment. */
export function loadEnergiatehokkuusOperationalLoop(
  buildingId: string,
  equipmentId: string,
): Promise<void> {
  return getSharedEnergiatehokkuusBuildingSnapshotCache().loadOperationalLoop(
    buildingId,
    equipmentId,
  );
}

/** UI adapter: apply Live optimization PATCH response onto the cached building. */
export function patchEnergiatehokkuusBuildingSnapshot(
  buildingId: string,
  building: Building,
): void {
  getSharedEnergiatehokkuusBuildingSnapshotCache().patchBuilding(
    buildingId,
    building,
  );
}
